import { Box } from '@mantine/core';
import { useCallback, useRef } from 'react';

import {
  SESSION_TOOLS_DEFAULT_WIDTH,
  SESSION_TOOLS_MAX_WIDTH,
  SESSION_TOOLS_MIN_WIDTH,
} from './sessionToolsDrawerConstants';

export interface SessionToolsResizeHandleProps {
  width: number;
  onWidthChange: (width: number, availableMainWidth: number) => void;
  onReset: () => void;
  availableMainWidth: number;
}

/** Same interaction model as AiAssistantResizeHandle. */
export function SessionToolsResizeHandle({
  width,
  onWidthChange,
  onReset,
  availableMainWidth,
}: SessionToolsResizeHandleProps): JSX.Element {
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);
      dragStartX.current = event.clientX;
      dragStartWidth.current = width;

      const onMove = (moveEvent: PointerEvent) => {
        const delta = dragStartX.current - moveEvent.clientX;
        onWidthChange(dragStartWidth.current + delta, availableMainWidth);
      };
      const onUp = (upEvent: PointerEvent) => {
        target.releasePointerCapture(upEvent.pointerId);
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
      };
      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
    },
    [availableMainWidth, onWidthChange, width],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? 40 : 16;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onWidthChange(width + step, availableMainWidth);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        onWidthChange(width - step, availableMainWidth);
      } else if (event.key === 'Home') {
        event.preventDefault();
        onReset();
      }
    },
    [availableMainWidth, onReset, onWidthChange, width],
  );

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize session tools"
      aria-valuenow={Math.round(width)}
      aria-valuemin={SESSION_TOOLS_MIN_WIDTH}
      aria-valuemax={SESSION_TOOLS_MAX_WIDTH}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onDoubleClick={onReset}
      title={`Drag to resize (double-click resets to ${SESSION_TOOLS_DEFAULT_WIDTH}px)`}
      style={{
        width: 1,
        flexShrink: 0,
        alignSelf: 'stretch',
        position: 'relative',
        background: 'var(--mantine-color-gray-4)',
      }}
    >
      <Box
        aria-hidden
        onPointerDown={onPointerDown}
        onDoubleClick={onReset}
        style={{
          position: 'absolute',
          insetBlock: 0,
          left: -4,
          width: 9,
          cursor: 'col-resize',
          touchAction: 'none',
        }}
      />
    </Box>
  );
}
